import { RNG } from '../core/rng';
import { P } from '../world/player';
import { EVOLUTIONS } from '../data/evolutions';
import { UNIONS } from '../data/unions';
import { passiveById } from '../data/passives';
import { rarityOf, rarityWeight, type Rarity } from '../data/rarity';
import { GRANT } from './meta-parts';
import {
  CRAFT_GATE_W, canTake, craftGateP, draftPoolIds, levelOf, passiveLevel, weaponById, weaponLevel
} from './loadout';
/* ============================================================
   抽卡決策（P1，2026-08-04）
   ============================================================

   在這之前，抽卡是 `ui/levelup.ts` 裡的一段均勻隨機（從可拿清單裡隨便挑三個）。
   那個寫法同時命中 `08-antipatterns.md` 的兩條毒性殺手：
   **#6 RNG 完全決定勝負且無緩解**、**#7 build 定型後選項退化**。

   ## 為什麼搬到模擬層

   1. **測得到**：權重演算法要能構造情境驗證（已持有 → 機率確實偏移），
      而 UI 層的函式沒有辦法在測試裡驅動。
   2. **埋得到點**：`sim/metrics.ts` 的被選率報表要分得出「沒被選」與「沒出現」。
   3. `lint:sim` 的決定性禁令掃得到這一層 —— 抽卡是對局的一部分，
      用 `Math.random` 會讓同一顆種子跑出不同的一場。

   `ui/levelup.ts` 從此只負責畫卡片。

   ## 演算法（`03-in-run-progression.md` 第二節，照抄）

     w  = 稀有度權重（隨時間偏移，史詩與傳說乘幸運）
     w *= 2.5   已持有且未滿級（build 收斂）
     w *= 4.0   拿了就湊齊某個進化／融合的前置（進化推力）
     w *= 1 - 0.35 × 最近出現次數（反重複）

   然後 `applyPity()` 做兩條保底。

   ## 進化推力為什麼判「拿了就完成」而不是「屬於某個配方」

   照 03 的原文（`completes_evolution_prereq`）。判成「屬於配方」的話，
   四個進化牽涉的 8 個項目會從第一次抽卡就一直吃 ×4.0 —— 那不是推力，
   是把池子的一大半永久加權，build 收斂與反重複都會被它蓋掉。

   同一個判定順便給 UI 用（`draftHints()`）：03 說進化前置達成的視覺標記
   「**這是最重要的引導**」，而現在進化只從寶箱出，玩家更不知道自己快湊齊了。 */

/** 這一場的抽卡狀態。重置責任在 `resetDraft()`，由 `resetLoadout()` 呼叫。 */
export var DRAFT = {
  /** 最近幾輪實際顯示出來的選項（反重複用）。只留 `RECENT_KEEP` 輪。 */
  recent: [] as string[][],
  /** 連續幾輪沒有出現武裝類。 */
  noWeapon: 0,
  /** 連續幾輪沒有出現回復類。 */
  noHeal: 0,
  /** 這一場被玩家永久排除的 id。 */
  banished: [] as string[],
  rerolls: 0,
  banishes: 0,
  /** 這一輪有哪些選項「拿了就湊齊進化／融合」。給 UI 標記用。 */
  hints: [] as string[]
};

/** 反重複看幾輪。3 輪是 `1 − 0.35n` 這個式子的自然邊界
    （第 3 次出現權重只剩 0.05，再往前看沒有意義）。 */
var RECENT_KEEP = 3;

/** 保底的「回復類」。按優先序 —— 這一條的意義是玩家快死了，
    要給的是活下去的手段，不是結構上限那種長線投資。 */
var HEAL_IDS = ['p-repair', 'p-armor', 'p-frame'];

export function resetDraft() {
  DRAFT.recent.length = 0;
  DRAFT.noWeapon = 0;
  DRAFT.noHeal = 0;
  DRAFT.banished.length = 0;
  /* 初始 2／1 是 03 的建議值。**Banish 要吝嗇** —— 它是「編輯機率空間」，
     無限 banish 會讓每一場都湊得到最佳 build，重玩價值歸零。
     `GRANT` 的兩個欄位是給局外部件補充用的接口（P4 才接上供給端）。 */
  DRAFT.rerolls = 2 + GRANT.rerolls;
  DRAFT.banishes = 1 + GRANT.banishes;
  DRAFT.hints.length = 0;
}

function isWeapon(id) { return !!weaponById(id); }
function isHeal(id) { return HEAL_IDS.indexOf(id) >= 0; }

/** 距離湊齊最近的那一條質變，還缺的**其中一項**（拿了會前進一步的東西）。

    2026-08-06 加的。銀箱／金箱的第一格保證是合成品，但那條規則的前提是
    「已經有配方湊齊了」（`craftIds()` 非空）—— 在那之前那一格就是普通抽卡，
    **玩家完全沒有朝質變前進的導引**。抽卡那一側的 ×4 推力（`craftCompletedBy`）
    也只在「差最後一級」才觸發，差兩級以上一樣沒有。

    於是保證從「保證給你合成品」升級成「保證給你朝合成品的一步」。

    判準是**還差幾級的總和**（武裝側 ＋ 被動側），最小的那一條優先。
    只回傳現在拿得到的項目（`canTake`）—— 槽位滿了拿不到的東西放進去
    只會變成一格死選項，而那正是這一格要解決的問題。

    回 `null` 代表沒有任何一條質變是「還缺東西而且缺的拿得到」的。 */
export function nearestCraftStepId(): string | null {
  var best: { need: number, ids: string[] } | null = null;
  function consider(need: number, ids: string[]) {
    var ok: string[] = [];
    for (var j = 0; j < ids.length; j++) if (ids[j] && canTake(ids[j])) ok.push(ids[j]);
    if (!ok.length) return;
    /* `need` 相同時**先到先得**（陣列順序），不擲骰 —— 擲骰會多消耗一個
       RNG 值，讓同一顆種子的既有行為（含黃金雜湊）整條漂掉。
       候選之間的隨機性由下面挑「缺哪一項」時的那一次擲骰提供。 */
    if (!best || need < best.need) best = { need: need, ids: ok };
  }
  var i;
  for (i = 0; i < EVOLUTIONS.length; i++) {
    var e = EVOLUTIONS[i];
    if (weaponLevel(e.id) > 0) continue;                 // 已經做出來了
    var wl = weaponLevel(e.evo.from), pl = passiveLevel(e.evo.needs);
    var pm = craftGateP(passiveById(e.evo.needs));
    var needE = Math.max(0, CRAFT_GATE_W - wl) + Math.max(0, pm - pl);
    if (!needE) continue;                                // 已經湊齊，走 craftIds() 那條路
    consider(needE, [wl < CRAFT_GATE_W ? e.evo.from : '', pl < pm ? e.evo.needs : '']);
  }
  for (i = 0; i < UNIONS.length; i++) {
    var u = UNIONS[i];
    if (weaponLevel(u.id) > 0) continue;
    var la = weaponLevel(u.union.a), lb = weaponLevel(u.union.b);
    var needU = Math.max(0, CRAFT_GATE_W - la) + Math.max(0, CRAFT_GATE_W - lb);
    if (!needU) continue;
    consider(needU, [la < CRAFT_GATE_W ? u.union.a : '', lb < CRAFT_GATE_W ? u.union.b : '']);
  }
  if (!best) return null;
  var pick: string[] = (best as any).ids;
  return pick.length === 1 ? pick[0] : pick[Math.floor(RNG.next() * pick.length) % pick.length];
}

/** 拿了這一項，某個進化或融合的前置就會湊齊嗎。

    只看「這一次就完成」：來源武裝差最後一級、或鑰匙被動差最後一級。
    已經成立的配方不算（那個進化已經在寶箱池裡等著了）。 */
export function craftCompletedBy(id): string | null {
  var i;
  for (i = 0; i < EVOLUTIONS.length; i++) {
    var e = EVOLUTIONS[i];
    if (weaponLevel(e.id) > 0) continue;             // 已經進化過了
    var key = passiveById(e.evo.needs);
    var wLv = weaponLevel(e.evo.from), pLv = passiveLevel(e.evo.needs);
    /* 門檻走 `craftGateP`／`CRAFT_GATE_W`，不是各自的等級上限 ——
       兩者在 2026-08-06 拆成兩個常數，這裡漏改的話「差最後一級」的推力
       會對著錯的級數判定，而且沒有任何錯誤訊息。 */
    var pMax = craftGateP(key);
    if (wLv >= CRAFT_GATE_W && pLv >= pMax) continue;   // 前置已達成
    if (id === e.evo.from && wLv === CRAFT_GATE_W - 1 && pLv >= pMax) return e.id;
    if (id === e.evo.needs && pLv === pMax - 1 && wLv >= CRAFT_GATE_W) return e.id;
  }
  for (i = 0; i < UNIONS.length; i++) {
    var u = UNIONS[i];
    if (weaponLevel(u.id) > 0) continue;
    var a = weaponLevel(u.union.a), b = weaponLevel(u.union.b);
    if (a >= CRAFT_GATE_W && b >= CRAFT_GATE_W) continue;
    if (id === u.union.a && a === CRAFT_GATE_W - 1 && b >= CRAFT_GATE_W) return u.id;
    if (id === u.union.b && b === CRAFT_GATE_W - 1 && a >= CRAFT_GATE_W) return u.id;
  }
  return null;
}

/** 回傳 id 而不是 bool，是因為 UI 要寫出**湊齊的是哪一個**
    —— 只說「快湊齊了」而不說是什麼，玩家還是不知道自己在追什麼。 */
export function completesCraft(id): boolean { return !!craftCompletedBy(id); }

/** 這一項最近幾輪出現過幾次。 */
function recentCount(id) {
  var n = 0;
  for (var i = 0; i < DRAFT.recent.length; i++) {
    for (var j = 0; j < DRAFT.recent[i].length; j++) if (DRAFT.recent[i][j] === id) n++;
  }
  return n;
}

/** 單一項目的權重。`counts` 見 `data/rarity.ts` 的 `rarityWeight`。 */
export function weightOf(id, tMin: number, counts: Record<Rarity, number>, boost?: number) {
  var luck = (P.s && P.s.luck) ? P.s.luck : 1;
  var r = rarityOf(id);
  var w = rarityWeight(r, tMin, luck, counts);
  if (w <= 0) return 0;
  /* 箱種給的加成（金箱 ×2）。**只乘史詩與傳說** —— 乘全部等於沒乘，
     而 Boss 的獎勵要看得出比較好。跟幸運同一個道理（見 `data/rarity.ts`）。 */
  if (boost && boost !== 1 && (r === 'epic' || r === 'legendary')) w *= boost;
  /* build 收斂：已持有且未滿級（滿級的已被 `availableIds()` 濾掉）。
     退場條件寫在 `06-POLISH-ROADMAP.md` 的 P1 段 —— 若五顆種子的
     build 多樣性下降，這個 2.5 要降到 1.8，兩輪都不行就整條拿掉。 */
  if (levelOf(id) > 0) w *= 2.5;
  if (completesCraft(id)) w *= 4.0;
  /* 反重複。三次之後是負數，夾在一個小的正值 ——
     夾成 0 的話「池子只剩三個」時會全部歸零，抽不出任何東西。 */
  var rep = 1 - 0.35 * recentCount(id);
  w *= (rep > 0.05 ? rep : 0.05);
  return w;
}

/** 加權取一個索引（不放回由呼叫端負責 splice）。 */
function pickWeighted(ids: string[], ws: number[]) {
  var total = 0, i;
  for (i = 0; i < ws.length; i++) total += ws[i];
  if (!(total > 0)) return Math.floor(RNG.next() * ids.length) % ids.length;
  var r = RNG.next() * total;
  for (i = 0; i < ids.length; i++) {
    r -= ws[i];
    if (r <= 0) return i;
  }
  return ids.length - 1;
}

/** 候選池：可以拿的、且沒被 banish 的。
    `noCraftLv` 由寶箱那條路徑帶進來（質變等級是升級選單專屬，
    理由見 `sim/loadout.ts` 的 `craftIds()`）。 */
function candidates(noCraftLv?: boolean) {
  var all = draftPoolIds(noCraftLv), out: string[] = [];
  for (var i = 0; i < all.length; i++) {
    if (DRAFT.banished.indexOf(all[i]) < 0) out.push(all[i]);
  }
  return out;
}

function countsOf(ids: string[]) {
  var c: any = { common: 0, rare: 0, epic: 0, legendary: 0 };
  for (var i = 0; i < ids.length; i++) c[rarityOf(ids[i])]++;
  return c;
}

/** 加權抽 k 個不重複。**寶箱也用它**（`sim/chest.ts`）——
    寶箱自己均勻隨機抽的話，`data/rarity.ts` 那張稀有度表會被整個繞過去，
    而且是靜默的：開出來的東西看起來完全正常，只有分佈是錯的。 */
export function sampleDraft(k: number, tMin: number, boost?: number) { return sample(k, tMin, boost, true); }

function sample(k: number, tMin: number, boost?: number, noCraftLv?: boolean) {
  var pool = candidates(noCraftLv), out: string[] = [];
  while (pool.length && out.length < k) {
    /* **每抽一張都重算 counts**：抽走一個史詩之後，剩下的史詩要分掉
       同一份目標比例。不重算的話分母偏大，高稀有度會被系統性低估。 */
    var counts = countsOf(pool);
    var ws: number[] = [];
    for (var i = 0; i < pool.length; i++) ws.push(weightOf(pool[i], tMin, counts, boost));
    var k2 = pickWeighted(pool, ws);
    out.push(pool[k2]);
    pool.splice(k2, 1);
  }
  return out;
}

/** 兩條保底（03 第二節的 `apply_pity`）。

    保底的意義不是慈善，是**移除最惡性的 RNG 失敗** ——
    玩家可以接受「我選錯了」，不能接受「遊戲根本沒給我機會」。 */
function applyPity(picks: string[], tMin: number) {
  var pool = candidates(), i, cand: string[] = [];
  /* 連續 3 輪沒出武裝 → 強制置換一個。 */
  var hasW = false;
  for (i = 0; i < picks.length; i++) if (isWeapon(picks[i])) hasW = true;
  if (DRAFT.noWeapon >= 3 && !hasW) {
    for (i = 0; i < pool.length; i++) {
      if (isWeapon(pool[i]) && picks.indexOf(pool[i]) < 0) cand.push(pool[i]);
    }
    if (cand.length) {
      var counts = countsOf(cand), ws: number[] = [];
      for (i = 0; i < cand.length; i++) ws.push(weightOf(cand[i], tMin, counts));
      picks[picks.length - 1] = cand[pickWeighted(cand, ws)];
      DRAFT.noWeapon = 0;
    }
  }
  /* 結構 < 30% 且連續 2 輪沒出回復 → 塞一個回復進去。
     **放最後一格**，跟武裝保底同一格是刻意的：兩條同時觸發時，
     快死了比「好久沒看到武裝」更急。 */
  var ratio = P.hpMax > 0 ? P.hp / P.hpMax : 1;
  var hasH = false;
  for (i = 0; i < picks.length; i++) if (isHeal(picks[i])) hasH = true;
  if (ratio < 0.30 && DRAFT.noHeal >= 2 && !hasH) {
    for (i = 0; i < HEAL_IDS.length; i++) {
      if (pool.indexOf(HEAL_IDS[i]) >= 0 && picks.indexOf(HEAL_IDS[i]) < 0) {
        picks[picks.length - 1] = HEAL_IDS[i];
        DRAFT.noHeal = 0;
        break;
      }
    }
  }
  return picks;
}

/** 記帳：把這一輪的結果推進 recent 與兩個保底計數。 */
function record(picks: string[]) {
  var i, hasW = false, hasH = false;
  for (i = 0; i < picks.length; i++) {
    if (isWeapon(picks[i])) hasW = true;
    if (isHeal(picks[i])) hasH = true;
  }
  DRAFT.noWeapon = hasW ? 0 : DRAFT.noWeapon + 1;
  DRAFT.noHeal = hasH ? 0 : DRAFT.noHeal + 1;
  DRAFT.recent.push(picks.slice());
  while (DRAFT.recent.length > RECENT_KEEP) DRAFT.recent.shift();
  DRAFT.hints = picks.filter(completesCraft);
}

/** 抽一輪。`tMin` 由呼叫端給（模擬層不該自己讀時鐘）。 */
export function rollDraft(tMin: number, k?: number) {
  /* 選項數吃局外部件（感應器）。它不改變強度，改變的是**選擇權** ——
     局外成長裡最有價值的東西不是傷害，是「這一場比較不會被爛手氣毀掉」。 */
  var want = k === undefined ? 3 + GRANT.choices : k;
  var picks = applyPity(sample(want, tMin), tMin);
  record(picks);
  return picks;
}

/** 重畫同一輪（不消耗任何次數、不推進保底計數）。

    **不推進保底計數**是關鍵：重畫算成新的一輪的話，玩家可以靠反覆重畫
    把武裝保底刷出來。但要覆蓋 `recent` 的最後一項 ——
    反重複看的是「玩家實際看過什麼」，被換掉的那一組他確實看過，
    不過留著會讓同一輪的兩次擲骰互相降權，那不是反重複的用意。 */
function redraw(tMin: number, k?: number) {
  if (DRAFT.recent.length) DRAFT.recent.pop();
  var want = k === undefined ? 3 + GRANT.choices : k;
  var picks = applyPity(sample(want, tMin), tMin);
  DRAFT.recent.push(picks.slice());
  DRAFT.hints = picks.filter(completesCraft);
  return picks;
}

/** 重抽這一輪。回傳新的一組；沒次數就回傳 null（呼叫端不要當成空選項）。 */
export function rerollDraft(tMin: number, k?: number) {
  if (DRAFT.rerolls <= 0) return null;
  DRAFT.rerolls--;
  return redraw(tMin, k);
}

/** 永久排除一個選項（本局內），並補一張回來。回傳新的一組或 null。

    **補位不消耗 reroll**：banish 的成本是 banish 自己那一次。
    不補的話玩家會看到一個空位 —— 用掉一次資源換來更少的選項，
    讀起來像懲罰，而 03 說 banish 是「編輯機率空間」的工具。 */
export function banishOption(id, tMin: number, k?: number) {
  if (DRAFT.banishes <= 0 || DRAFT.banished.indexOf(id) >= 0) return null;
  DRAFT.banishes--;
  DRAFT.banished.push(id);
  return redraw(tMin, k);
}

/** 給 UI：這一輪哪幾張要發光（拿了就湊齊進化／融合）。 */
export function draftHints() { return DRAFT.hints.slice(); }
